import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { Heart, ShoppingCart, Star, ShieldCheck, Sparkles, MessageCircle, Scale, Eye, Zap } from 'lucide-react';
import { formatINR } from '../data/products';
import { resolveProductImage, handleProductImageError } from '../utils/imageHelper';

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { 
    addToCart, 
    wishlist, 
    toggleWishlist, 
    masterMetalRates,
    calculateProductPrice,
    getMasterRateForProduct,
    storeSettings,
    addToast
  } = useShop();

  const pricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : { finalPrice: product?.price };
  const effectiveRate = pricing?.ratePerGram || (getMasterRateForProduct ? getMasterRateForProduct(product, masterMetalRates) : 0);
  const livePrice = pricing?.finalPrice || product?.price || 0;
  const isFavorited = Boolean(product?.id && Array.isArray(wishlist) && wishlist.includes(product.id));

  // Compute MRP and Discount %
  const originalMRP = product?.originalPrice || Math.round(livePrice * 1.15);
  const discountPercent = Math.max(5, Math.round(((originalMRP - livePrice) / originalMRP) * 100));

  const rawImage = (Array.isArray(product?.images) && product.images[0]) || product?.image;
  const primaryImage = resolveProductImage(rawImage, product);

  const purityStr = String(product?.purity || product?.goldPurity || product?.metal || '');
  const hallmarkText = purityStr.includes('916') 
    ? '22K 916' 
    : purityStr.includes('750') 
    ? '18K 750' 
    : purityStr.includes('585') 
    ? '14K 585' 
    : purityStr.includes('925') 
    ? '925 Silver' 
    : 'Hallmarked';

  const isKitchenware = product?.category === 'kitchenware';

  return (
    <div 
      className="product-card fk-product-card"
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '2px',
        border: '1px solid #f0f0f0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }}
      onClick={() => navigate(`/product/${product.id}`)}
    >
      {/* Top Image Container */}
      <div 
        style={{
          position: 'relative',
          height: '240px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px'
        }}
        className="prod-card-media"
      >
        <img
          src={primaryImage}
          alt={product?.name || 'Jewellery'}
          onError={(e) => handleProductImageError(e, product)}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transition: 'transform 0.3s ease'
          }}
          className="prod-thumb"
        />

        {/* Top Badges */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          zIndex: 2
        }}>
          {product?.badge && (
            <span style={{
              background: '#ffe500',
              color: '#2874f0',
              fontSize: '0.62rem',
              fontWeight: 800,
              padding: '2px 6px',
              borderRadius: '2px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              textTransform: 'uppercase'
            }}>
              {product.badge}
            </span>
          )}

          {/* Flipkart / ML Assured Badge */}
          <span style={{
            background: '#2874f0',
            color: '#ffffff',
            fontSize: '0.62rem',
            fontWeight: 800,
            padding: '2px 6px',
            borderRadius: '2px',
            fontStyle: 'italic',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px'
          }}>
            ML-Assured ★
          </span>
        </div>

        {/* Wishlist Heart Icon Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (product?.id) toggleWishlist(product.id);
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 3,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.15s ease'
          }}
          title={isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist'}
        >
          <Heart
            size={16}
            color={isFavorited ? '#ff6161' : '#878787'}
            fill={isFavorited ? '#ff6161' : 'none'}
          />
        </button>
      </div>

      {/* Product Content Details */}
      <div style={{
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        borderTop: '1px solid #f8f9fa'
      }}>
        {/* Title */}
        <h3 
          style={{
            fontSize: '0.92rem',
            fontWeight: 500,
            color: '#212121',
            marginBottom: '6px',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.5em'
          }}
          className="fk-product-title"
          title={product.name}
        >
          {product.name}
        </h3>

        {/* Rating Badge & Purity */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          fontSize: '0.78rem'
        }}>
          {/* Flipkart Signature Green Rating Pill */}
          <span style={{
            backgroundColor: '#388e3c',
            color: '#ffffff',
            fontSize: '0.72rem',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '3px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px'
          }}>
            {product.rating || '4.8'} <Star size={10} fill="#ffffff" color="#ffffff" />
          </span>

          <span style={{ color: '#878787', fontSize: '0.75rem' }}>
            ({Math.floor(Number(product.rating || 4.8) * 120 + 35)})
          </span>

          {/* Hallmark Purity Tag */}
          <span style={{
            fontSize: '0.72rem',
            color: '#555555',
            fontWeight: 600,
            backgroundColor: '#f1f3f6',
            padding: '1px 6px',
            borderRadius: '2px',
            marginLeft: 'auto'
          }}>
            {hallmarkText}
          </span>
        </div>

        {/* Flipkart Price Block: Bold Price + Strikethrough MRP + Green Discount */}
        <div style={{ marginTop: 'auto', paddingTop: '4px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '4px'
          }}>
            <span style={{
              fontSize: '1.2rem',
              fontWeight: 800,
              color: '#212121',
              fontFamily: 'inherit'
            }}>
              {formatINR(livePrice)}
            </span>

            <span style={{
              fontSize: '0.82rem',
              color: '#878787',
              textDecoration: 'line-through'
            }}>
              {formatINR(originalMRP)}
            </span>

            <span style={{
              fontSize: '0.82rem',
              color: '#388e3c',
              fontWeight: 700
            }}>
              {discountPercent}% off
            </span>
          </div>

          <div style={{
            fontSize: '0.72rem',
            color: '#212121',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ color: '#388e3c', fontWeight: 600 }}>Free delivery</span>
            <span style={{ color: '#878787' }}>• Daily Low Price</span>
          </div>

          {/* Action Buttons: Yellow Add to Cart & Orange Buy Now */}
          <div className="prod-cta-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
            <button
              className="prod-cta-btn prod-cta-cart"
              onClick={() => {
                addToCart({
                  ...product,
                  price: livePrice
                }, 1);
                addToast(`Added ${product.name} to Cart!`, 'success');
              }}
              style={{
                backgroundColor: '#ff9f00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '2px',
                fontSize: '0.78rem',
                fontWeight: 700,
                padding: '8px 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f39700'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff9f00'}
            >
              <ShoppingCart size={13} /> Add to Cart
            </button>

            <button
              className="prod-cta-btn prod-cta-buy"
              onClick={(e) => {
                e.stopPropagation();
                addToCart({
                  ...product,
                  price: livePrice
                }, 1);
                addToast(`Proceeding to checkout for ${product.name}!`, 'success');
                navigate('/checkout');
              }}
              style={{
                backgroundColor: '#fb641b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '2px',
                fontSize: '0.78rem',
                fontWeight: 700,
                padding: '8px 4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eb5a13'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fb641b'}
            >
              <Zap size={13} /> Buy Now
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .prod-card-media {
            height: 150px !important;
            padding: 8px !important;
          }
          .prod-cta-btn {
            font-size: 0.68rem !important;
            padding: 7px 2px !important;
            gap: 2px !important;
          }
          .prod-cta-btn svg {
            width: 11px !important;
            height: 11px !important;
          }
          .fk-product-info {
            padding: 8px !important;
          }
        }
        .fk-product-card:hover .prod-thumb {
          transform: scale(1.04);
        }
        .fk-product-card:hover .fk-product-title {
          color: #2874f0;
        }
      `}</style>
    </div>
  );
}
